import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FileCheck2 } from 'lucide-react';
import type { DxlRechtstexteKonto, PlattformRechtstextAnsicht } from '@dxl/shared';
import { Button, Dialog, Hinweis, toast } from '@dxl/ui';
import { api, ApiFehler } from '../lib/api';
import { RechtstextDialog } from './RechtstextDialog';
import '../styles/rechtstexte.css';

const K = '/api/konto/dxl-rechtstexte';
const SCHLUESSEL = ['konto', 'dxl-rechtstexte'];
const GESEHEN = 'dxl-zustimmung-gesehen';

function gesehen(): boolean {
  try {
    return sessionStorage.getItem(GESEHEN) === '1';
  } catch {
    return false;
  }
}
function merken(): void {
  try {
    sessionStorage.setItem(GESEHEN, '1');
  } catch {
    /* ohne Speicher erscheint der Dialog beim nächsten Laden erneut */
  }
}

/**
 * Hinweis im Konto des Marktplatzes, wenn DXL neue AGB oder einen neuen AVV veröffentlicht hat (bzw. beim ersten
 * Anmelden). Der Inhaber stimmt im Dialog zu; gespeichert werden Version, Zeit und Nutzer. Andere Nutzer sehen den
 * Hinweis ohne Zustimmen-Knopf, in der DXL-Ansicht erscheint nichts.
 */
export function DxlZustimmung() {
  const qc = useQueryClient();
  const daten = useQuery({ queryKey: SCHLUESSEL, queryFn: () => api<DxlRechtstexteKonto>(K), staleTime: 5 * 60_000 });
  const [offen, setOffen] = useState(false);
  const [gelesen, setGelesen] = useState<PlattformRechtstextAnsicht | null>(null);
  const [haken, setHaken] = useState(false);

  const k = daten.data;
  const ausstehend = k?.dokumente.filter((d) => k.offen.includes(d.dokument as never)) ?? [];

  useEffect(() => {
    if (k?.darfZustimmen && k.offen.length && !gesehen()) setOffen(true);
  }, [k]);

  const lesen = useMutation({
    mutationFn: (dokument: string) => api<PlattformRechtstextAnsicht>(`${K}/${dokument}`),
    onSuccess: setGelesen,
    onError: (e) => toast(e instanceof ApiFehler ? e.message : 'Dokument konnte nicht geladen werden.', 'fehler'),
  });
  const zustimmen = useMutation({
    mutationFn: () => api<DxlRechtstexteKonto>(`${K}/zustimmen`, { methode: 'POST', body: { dokumente: ausstehend.map((d) => ({ dokument: d.dokument, version: d.version })) } }),
    onSuccess: (r) => {
      qc.setQueryData(SCHLUESSEL, r);
      setOffen(false);
      setHaken(false);
      toast('Zustimmung gespeichert');
    },
    onError: (e) => {
      // neue Fassung inzwischen veröffentlicht: neu laden, Dialog bleibt offen
      void qc.invalidateQueries({ queryKey: SCHLUESSEL });
      toast(e instanceof ApiFehler ? e.message : 'Zustimmung fehlgeschlagen.', 'fehler');
    },
  });

  if (!k || !ausstehend.length) return null;
  const schliessen = () => {
    merken();
    setOffen(false);
  };
  const neu = ausstehend.some((d) => d.neueFassung);

  return (
    <>
      <div className="dxl-zustimmung" role="status">
        <FileCheck2 aria-hidden />
        <span className="dxl-zustimmung__text">
          <strong>{neu ? 'Neue Vertragsunterlagen von DXL' : 'Vertragsunterlagen von DXL'}:</strong>{' '}
          {ausstehend.map((d) => d.titel).join(' und ')}
          {k.darfZustimmen ? ' – bitte lesen und zustimmen.' : ' – die Zustimmung erteilt der Inhaber des Marktplatzes.'}
        </span>
        <Button klein art={k.darfZustimmen ? 'primaer' : 'sekundaer'} onClick={() => setOffen(true)}>{k.darfZustimmen ? 'Ansehen und zustimmen' : 'Ansehen'}</Button>
      </div>

      <Dialog
        gross
        breite={640}
        offen={offen && !gelesen}
        titel={neu ? 'Neue Fassung der Vertragsunterlagen' : 'Vertragsunterlagen von DXL'}
        onSchliessen={schliessen}
        fuss={
          <div className="knopfreihe knopfreihe--rechts">
            <Button art="sekundaer" onClick={schliessen}>Später</Button>
            {k.darfZustimmen && (
              <Button laedt={zustimmen.isPending} disabled={!haken} onClick={() => zustimmen.mutate()}>Zustimmen</Button>
            )}
          </div>
        }
      >
        <div className="formular">
          <p>
            {neu
              ? 'DXL hat neue Fassungen veröffentlicht. Für die weitere Nutzung der Plattform benötigen wir Ihre Zustimmung zur aktuellen Fassung.'
              : 'Für die Nutzung der Plattform benötigen wir die Zustimmung des Marktplatzes zu den folgenden Dokumenten.'}
          </p>
          <ul className="dxl-zustimmung__liste">
            {ausstehend.map((d) => (
              <li key={d.dokument}>
                <span>
                  <strong>{d.titel}</strong>
                  <span className="dxl-zustimmung__meta">Version <span className="mono">{d.version}</span> · Stand {d.stand}{d.neueFassung && ' · neue Fassung'}</span>
                </span>
                <Button art="sekundaer" klein laedt={lesen.isPending && lesen.variables === d.dokument} onClick={() => lesen.mutate(d.dokument)}>Lesen</Button>
              </li>
            ))}
          </ul>
          {k.darfZustimmen ? (
            <label className="schalterzeile">
              <input type="checkbox" checked={haken} onChange={(e) => setHaken(e.target.checked)} />
              <span>Ich habe die Dokumente gelesen und stimme ihnen im Namen des Marktplatzes zu.</span>
            </label>
          ) : (
            <Hinweis art="info">Zustimmen kann nur der Inhaber des Marktplatzes.</Hinweis>
          )}
        </div>
      </Dialog>

      <RechtstextDialog text={gelesen} onSchliessen={() => setGelesen(null)} />
    </>
  );
}
